/**
 * Status-pill label maps — framework-free so they unit-test under the node-only
 * vitest setup (the component itself is e2e-only; see vitest.config.ts).
 *
 * Two label sets keyed off the DraftState union:
 *  - CREATOR_LABEL: what every creator sees on the pill. No engineering jargon
 *    ("PR open"/"Partial merge"/"Building" leaked the publish plumbing).
 *  - STATE_DETAIL: the precise technical phrasing, surfaced to admins as a hover
 *    tooltip so they keep the full picture without a second visual language.
 *
 * KIND (the colour tone) is unchanged. The exhaustiveness test over
 * ALL_DRAFT_STATES is the drift-guard: add a state to the union without a label
 * and CI fails.
 */
import { DraftState } from '../../services/agent-builder.interface';

export type PillKind = 'draft' | 'review' | 'progress' | 'live' | 'attention' | 'editing';

/** Every DraftState — kept in sync with the union (agent-builder.interface.ts),
 *  which itself mirrors studio-api drafts/app.py state machine. */
export const ALL_DRAFT_STATES: DraftState[] = [
  'draft', 'pending_approval', 'approved', 'publishing', 'pr_open', 'partial_merge',
  'building', 'deploying', 'live', 'promoted', 'failed', 'broken', 'rejected', 'withdrawn',
  'editing', 'deleting',
];

export const KIND: Record<DraftState, PillKind> = {
  draft: 'draft',
  pending_approval: 'review',
  approved: 'review',
  publishing: 'progress',
  pr_open: 'progress',
  partial_merge: 'attention',
  building: 'progress',
  deploying: 'progress',
  live: 'live',
  promoted: 'live',
  failed: 'attention',
  broken: 'attention',
  rejected: 'attention',
  withdrawn: 'draft',
  editing: 'editing',
  deleting: 'attention',
};

/** Creator-facing copy — collapses the publish plumbing into plain progress
 *  language. In-flight states read "Publishing…/Setting up…"; both error states
 *  read "Needs attention" (the actionable detail lives in the agent-detail
 *  banner). `pr_open` reads "Publishing…" (not "Submitting…") — the backend jumps
 *  straight to `pr_open` after approval, so it's the first thing a creator sees
 *  under the "Publishing" tab and must match it. No "PR", "merge", "build", or
 *  "deploy" leaks to non-engineers. */
export const CREATOR_LABEL: Record<DraftState, string> = {
  draft: 'Draft',
  pending_approval: 'In review',
  approved: 'Approved',
  publishing: 'Publishing…',
  pr_open: 'Publishing…',
  partial_merge: 'Setting up…',
  building: 'Setting up…',
  deploying: 'Setting up…',
  live: 'Live',
  promoted: 'Promoted to prod',
  failed: 'Needs attention',
  broken: 'Needs attention',
  rejected: 'Changes requested',
  withdrawn: 'Withdrawn',
  editing: 'Editing',
  deleting: 'Removing…',
};

/** Precise technical state — admin-only hover tooltip. */
export const STATE_DETAIL: Record<DraftState, string> = {
  draft: 'Draft',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  publishing: 'Publishing',
  pr_open: 'PR open',
  partial_merge: 'Partial merge (PR1 merged, PR2 pending)',
  building: 'Building',
  deploying: 'Deploying',
  live: 'Live',
  promoted: 'Promoted to prod (prod rollout runs in the prod account / prod Studio)',
  failed: 'Failed',
  broken: 'Broken (PR2 merged without PR1)',
  rejected: 'Changes requested',
  withdrawn: 'Withdrawn',
  editing: 'Editing',
  deleting: 'Deleting',
};

export function pillKind(state: DraftState): PillKind {
  return KIND[state] ?? 'draft';
}

/** Visible pill label — creator-friendly for everyone (admins included). */
export function pillLabel(state: DraftState): string {
  return CREATOR_LABEL[state] ?? state;
}

/** Technical phrasing for the admin tooltip. */
export function stateDetail(state: DraftState): string {
  return STATE_DETAIL[state] ?? state;
}
